import Key from "csi/core/Key";
import Spec from "csi/core/Spec";
import SpecBuilder from "csi/core/SpecBuilder";
import StoreValuesConstructor, { StoreValues } from "csi/store/Values";
import types from "csi/types";
import { arrayFromValue } from "csi/utility/arrayUtility";
import { isNullOrUndefined } from "csi/utility/coreUtility";
import { DateRangeSelection, DAY_IN_MS } from "csi/utility/dateUtility";
import { isBlankOperator, isNotBlankOperator } from "csi/utility/operatorUtility";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import { ILocaleProvider } from "designSystem/locale";
import { forEach, isEmpty, isNumber } from "lodash";
import { ICustomViewFilter, ICustomViewFilterCondition, ICustomViewFilterConditionV1, ICustomViewFilterGroup, ICustomViewFilterSettings, ICustomViewFilterSettingsV1, ICustomViewFilterV1, ICustomViewGroup } from "src/c8-platform/type/customView";
import { ATTR_NAME, C8NodeType } from "src/c8-platform/type/node";
import { Operator } from "src/c8-platform/type/operators";
import { attributeValueAsArray, C8AttributeKey, C8DateRangeArray, VALUE_TYPE } from "src/c8-platform/type/value";
import { RuntimeRulesSchema } from "../attributeRule/RuleService";
import RuleStore, { Rule } from "../attributeRule/RuleStore";
import CustomViewAttributesStore from "./attributes/CustomViewAttributesStore";
import { populateAttributeSpecsFromTargetClassDomain } from "./customViewService";
import { ICustomViewGalleryOptionsV1, ICustomViewQueryOptions } from "./options/CustomViewOptions";

export type GetValuesStoreFunction = (value: Spec) => StoreValues;

export const lessThanGreaterThanOps = [xml.OP_GREATER_THAN, xml.OP_GREATER_EQUAL, xml.OP_LESS_THAN, xml.OP_LESS_EQUAL];
export const stringNonTextFormatOperators = [xml.OP_EQUALS, xml.OP_NOT_EQUALS, xml.OP_CONTAINS, xml.OP_NOT_CONTAINS, xml.OP_MATCHES, xml.OP_NOT_MATCHES];

/** create rule store for given type in order to build query filters */
export function createRuleStore(type: C8NodeType, sourceType: C8NodeType, loc: ILocaleProvider, existingFilters?: ICustomViewFilter): RuleStore {
	const specBuilder = new SpecBuilder(type);
	const attributesStore = new CustomViewAttributesStore(loc);
	const targetClassSpecs = populateAttributeSpecsFromTargetClassDomain(type);

	attributesStore.addLevel({
		name: loc.type(type),
		specBuilder,
	});

	const getValuesStore: GetValuesStoreFunction = (spec: Spec): StoreValues => new StoreValuesConstructor({
		bEmptyItem: false,
		sortQuery: new Query().SortByNode(xml.PROP_NAME),
		spec,
		typeNode: type,
	});


	return new RuleStore(
		loc,
		targetClassSpecs,
		undefined,
		getValuesStore,
		existingFilters,
		true,
		attributesStore,
		false,
		null,
		null,
		true,
		sourceType,
	);
};

/** return the keys used to reference source values in relative filters */
export function getRelativeFilterKeys(filterSettings: ICustomViewFilter): C8AttributeKey[] {
	const keys: C8AttributeKey[] = [];

	const traverseCondition = (condition: ICustomViewFilterCondition): void => {
		const { relative, value } = condition as ICustomViewFilterCondition;
		if (relative) {
			const keyValue = arrayFromValue(value) as C8AttributeKey[];
			keys.push(...keyValue);
		}
	};

	filterSettings.C?.forEach((condition) => {
		const group = getCustomViewFilterGroup(condition);
		if (group) {
			group.conditions.forEach(traverseCondition);
		} else {
			traverseCondition(condition as ICustomViewFilterCondition);
		}
	});

	return keys;
}

/** return the Specs of all the keys on a spec's extended path */
export function getSpecForExtendedKeys(spec: Spec, specBuilder: SpecBuilder): Spec[] {
	const keys = Key.getExtendedPathKeys(spec?.getExtendedPath());
	return keys.map((key) => specBuilder.getSpecForKey(key));
}

/** convert floating days to date range selection days */
export function convertFloatingDaysToDateRangeSelection(value: number): C8DateRangeArray {
	const days = !value ? 0 : Math.round(Math.abs(value) / DAY_IN_MS);
	if (!days) {
		return [0, 0, DateRangeSelection.TODAY];
	}
	const dateRangeSelection = value < 0 ? DateRangeSelection.PAST_NUMBER_OF_DAYS : DateRangeSelection.FUTURE_NUMBER_OF_DAYS;
	return [null, null, dateRangeSelection, days];
}

/** convert array of floating days to date range selection days */
export function convertFloatingDaysToDateRangeSelectionArray(values: C8DateRangeArray): C8DateRangeArray[] {
	return values.map((value) => convertFloatingDaysToDateRangeSelection(value));
}

/** convert old custom view filter into the new custom view filter structure */
export function migrateFilterSettingsV1(filterSettings: ICustomViewFilterSettingsV1, specBuilder: SpecBuilder): ICustomViewFilterSettings {
	if (isEmpty(filterSettings)) {
		return {};
	}

	const newFilterSettings: ICustomViewFilterSettings = {};

	forEach(filterSettings, (value, key) => {
		const { C: rules, FD, ...otherSettings } = value;
		const filters = [];
		let groupNextFilter = false;
		let filterGroup = [];

		rules?.forEach((filter) => {
			const { group, ...otherProps } = filter;
			const spec = specBuilder.getSpecForKey(otherProps.key);
			// remove filter if the spec is part of a matrix. 
			if (!spec.isMatrix) {
				if (group) {
					groupNextFilter = true;
					filterGroup.push({ ...otherProps });
				} else if (groupNextFilter) {
					groupNextFilter = false;
					filterGroup.push({ ...otherProps });
					filters.push({ conditions: filterGroup, isAND: value.OR });
					filterGroup = [];
				} else {
					filters.push({ ...otherProps });
				}
			}
		});

		// if we only have 2 - 3 filters,
		// If we remove the first filter or the filters in the middle of the filter group, it will make the group smaller. The last filter in the group will push the group to filters
		// If we remove the last filter in the filter group, we don't push the group into filters. Therefore we need to check and push group into filters if necessary
		if (groupNextFilter) {
			filters.push({ conditions: filterGroup, isAND: value.OR });
		}

		if (FD) {
			const traverseCondition = (conditions: ICustomViewFilterCondition[], condition: ICustomViewFilterCondition, index: number): number => {
				let returnIndex = index;
				const { key, op, relative, value } = condition as ICustomViewFilterCondition;
				if (op === xml.OP_RANGE) {
					// convert a range to two filters, one "greater than" and one "less than"
					// the immediate next filter will be the "less than or equal to" filter
					// the current filter will be the "greater than or equal to" filter
					const values = convertFloatingDaysToDateRangeSelectionArray([value[0], value[1]] as C8DateRangeArray);
					condition.op = xml.OP_GREATER_EQUAL as Operator;
					condition.value = values[0];
					// this filter should be immediately followed by the "less than or equal to" filter
					conditions.splice(returnIndex, 0, { key, op: xml.OP_LESS_EQUAL as Operator, value: values[1], relative });
					returnIndex++;
				} else {
					condition.value = convertFloatingDaysToDateRangeSelection(value as number);
				}
				return returnIndex;
			};
			// convert floating dates to dynamic dates
			for (let index = 0; index < filters.length; index++) {
				const condition = filters[index];
				const group = getCustomViewFilterGroup(condition);
				if (group) {
					for (let i = 0; i < group.conditions.length; i++) {
						i = traverseCondition(group.conditions, group.conditions[i], i);
					}
				} else {
					index = traverseCondition(filters, condition as ICustomViewFilterCondition, index);
				}
			}
		}

		const newCustomViewFilterSettings: ICustomViewFilter = {
			C: filters,
			...otherSettings,
		};

		newFilterSettings[key] = newCustomViewFilterSettings;
	});

	return newFilterSettings;
}

/** Convert Rule to ICustomViewFilterCondition when we save filter settings */
export function convertRuleToFilterCondition(rule: Rule): ICustomViewFilterCondition | null {
	const { item, selectedOperator, selectedValues, spec, relativeType } = rule;
	let value: string | string[] = selectedValues[0]?.id;
	if (!relativeType && (spec?.type === xml.ATTR_TIME || selectedValues.length > 1)) {
		value = selectedValues.map((value) => value.value);
	}

	return {
		key: item.id,
		op: selectedOperator.id as Operator,
		relative: !!relativeType,
		value,
	};
}

/** migrate group settings for table view */
export function migrateTableViewGroupSettingsV1(options: ICustomViewQueryOptions): ICustomViewGroup {
	const { groupSettings } = options;

	// If either key already exist, no need for migration.
	if (groupSettings?.key1 || groupSettings?.key2) {
		return groupSettings;
	}
	
	const newGroupSettings: ICustomViewGroup = {};

	newGroupSettings.key1 = groupSettings[0]?.key;
	newGroupSettings.key2 = groupSettings[1]?.key;
	newGroupSettings.expand1 = groupSettings[0]?.expand;
	newGroupSettings.expand2 = groupSettings[1]?.expand;

	return newGroupSettings;
}

/** migrate group settings for gallery view */
export function migrateGalleryViewGroupSettingsV1(options: ICustomViewQueryOptions): ICustomViewGroup {
	const { groupSettings } = options;

	// If either key already exist, no need for migration.
	if (groupSettings?.key1 || groupSettings?.key2) {
		return groupSettings;
	}
	
	let newGroupSettings: ICustomViewGroup = {};

	const galleryOptions = options as ICustomViewGalleryOptionsV1;
	newGroupSettings = {
		key1: galleryOptions.columnGroupAttribute,
		key2: galleryOptions.rowGroupAttribute,
		expand1: galleryOptions.expandAllGroups,
		expand2: galleryOptions.expandAllGroups,
	};
	return newGroupSettings;
}

/** checks whether V1 and V2 Custom Views Filters should restore comparison operators on string and ref */
export function checkIfCustomViewContainsComparisonOperator(filterSettings: ICustomViewFilter, specBuilder: SpecBuilder): boolean {
	if (isEmpty(filterSettings)) {
		return false;
	}

	const { C: filters } = filterSettings;

	return filters?.some((filter) => {
		const group = getCustomViewFilterGroup(filter);
		if (group) {
			return group.conditions.some((groupFilter) => {
				return checkIfCustomViewFilterConditionContainsComparisonOperator(groupFilter, specBuilder);
			});
		} else {
			return checkIfCustomViewFilterConditionContainsComparisonOperator(filter as ICustomViewFilterCondition, specBuilder);
		}
	});
}

/** checks whether comparison operator exist on a custom view filter condition */
function checkIfCustomViewFilterConditionContainsComparisonOperator(filter: ICustomViewFilterCondition, specBuilder: SpecBuilder): boolean {
	const { key, op } = filter as ICustomViewFilterCondition;
	const spec = specBuilder.getSpecForKey(key);
	return (spec.type === VALUE_TYPE.REF || spec.type === VALUE_TYPE.STRING) && lessThanGreaterThanOps.some((operator) => operator === op);
}

/** check if the filter is a filter group or filter and return the filter group */
export function getCustomViewFilterGroup(filter: ICustomViewFilterCondition | ICustomViewFilterGroup): ICustomViewFilterGroup | undefined {
	const group = filter as ICustomViewFilterGroup;
	if (group.isAND !== undefined) {
		return group;
	}
}

/** convert RuntimeRulesSchema to Custom View Filters so rule store only need to handle one type of data structure */
export function convertRuntimeRulesSchemaToCustomFilters(runtimeRules: RuntimeRulesSchema): ICustomViewFilter {
	const or = runtimeRules.matchAny;
	const rules: ICustomViewFilterCondition[] = [];
	runtimeRules.ruleSpecs.map((rule) => {
		const { values, op } = rule.rule;
		rules.push({ value: values, key: rule.spec.uniqueId, op: op as Operator });
	});

	return {
		C: rules,
		FD: false,
		OR: or,
	};
}

/**
 * combine filters that share the same attribute and operators 
 */
export function combineFilters(filterObj: ICustomViewFilterV1, builder: SpecBuilder): void {
	let groupFilter = false;
	const filterMap = new Map();
	let filterGroup = new Map();
	let groupCount = 0;

	// combine filters and filters in filter groups separately and store them into a map
	filterObj.C.forEach((filter) => {
		// convert filters with no value to blank / not blank filters
		const spec = builder.getSpecForKey(filter.key);
		const key = [filter.key, getOperator(spec, filter), filter.relative].toString();

		if (filter.group) {
			_addingFitlersToMap(filterGroup, filter, key);
			groupFilter = true;
		} else if (groupFilter) {
			_addingFitlersToMap(filterGroup, filter, key);
			filterMap.set(groupCount, filterGroup);
			filterGroup = new Map();
			groupCount++;
			groupFilter = false;
		} else {
			_addingFitlersToMap(filterMap, filter, key);
		}
	});

	// construct new filterObj based on filterMap
	filterObj.C = [];
	filterMap.forEach((value, key) => {
		// if the key is a number, then it is a filter group instead of a single filter
		if (isNumber(key)) {
			let count = 0;
			let totalItemInFilterGroup = 0;
			value.forEach((item) => {
				totalItemInFilterGroup += item.length;
			});
			if (filterObj.OR) {
				// don't combine the filters when the operator between the groups is AND
				value.forEach((filter, key) => {
					const spec = extractSpecFromKey(key, builder);
					// special case for time attributes, we store an array of numeric values to specify time values. 
					// the procedure above would separate the time values. We need to combine it.
					if (spec.type === xml.ATTR_TIME) {
						count += filter.length;
						_reconstructFilterConditionsWhenConnectedByAndOperator(filterObj, key, filter, count < totalItemInFilterGroup);
					} else {
						filter.forEach((item) => {
							count++;
							_reconstructFilterConditionsWhenConnectedByAndOperator(filterObj, key, [item], count < totalItemInFilterGroup);
						});
					}
				});
			} else {
				// combine the filters when the operator between groups is OR
				value.forEach((filter, key) => {
					count += filter.length;
					const groupRecord = Array(filter.length).fill(true);
					if (count == totalItemInFilterGroup) {
						groupRecord[filter.length - 1] = false;
					}
					
					const spec = extractSpecFromKey(key, builder);
					// special case for time attributes, we store an array of numeric values to specify time values. 
					// the procedure above would separate the time values. We need to combine it.
					if (spec.type === xml.ATTR_TIME) {
						_reconstructFilterConditionsWhenConnectedByAndOperator(filterObj, key, filter, count < totalItemInFilterGroup);
					} else {
						_reconstructFilterConditions(filterObj, key, filter, groupRecord, builder);
					}
				});
			}
		} else if (!filterObj.OR) {
			// special case for time attributes, we store an array of numeric values to specify time values. 
			// the procedure above would separate the time values. We need to combine it.
			const spec = extractSpecFromKey(key, builder);
			if (spec.type === xml.ATTR_TIME) {
				_reconstructFilterConditionsWhenConnectedByAndOperator(filterObj, key, value, false);
			} else {
				// otherwise if OR is false, we don't combine the filters
				if (value.length === 0) {
					// case where the filter is a filter with no value (is blank / is not blank operator filter)
					_reconstructFilterConditionsWhenConnectedByAndOperator(filterObj, key, null, false);
				} else {
					value.forEach((item) => {
						_reconstructFilterConditionsWhenConnectedByAndOperator(filterObj, key, [item], false);
					});
				}
			}
		} else {
			// special case for time attributes, we store an array of numeric values to specify time values. 
			// the procedure above would separate the time values. We need to combine it.
			const spec = extractSpecFromKey(key, builder);
			if (spec.type === xml.ATTR_TIME) {
				_reconstructFilterConditionsWhenConnectedByAndOperator(filterObj, key, value, false);
			} else {
			// otherwise if OR is true, we combine the filters
				_reconstructFilterConditions(filterObj, key, value, [false], builder);
			}
		}
	});
}

/** combining filters by adding them to the map */
function _addingFitlersToMap(filterMap: Map<C8AttributeKey, C8AttributeKey[]>, filter: ICustomViewFilterConditionV1, key: C8AttributeKey): void {
	const filterValue = attributeValueAsArray(filter.value) as string[];
	if (filterMap.has(key)) {
		filterMap.set(key, [...filterMap.get(key), ...filterValue]);
	} else {
		filterMap.set(key, filterValue);
	}
}

/** return the plural form of operators, for filters that are combined */
function _operatorAfterCombiningFilters(operator: string): string {
	if (operator === xml.OP_EQUALS) {
		return xml.OP_CONTAINS;
	}
	else if (operator === xml.OP_NOT_EQUALS) {
		return xml.OP_NOT_CONTAINS;
	}

	return operator;
}


/** adding filter to filter conditions */
function _pushFilterToFilterConditions(filterObj: ICustomViewFilterV1, key: string, op: string, value: string[], relative: boolean, group: boolean): void {
	// if the operator it not (contains or not contains) and the length of the array is 1, take the value out of the array
	const filterValue = op !== Operator.CONTAINS && op !== Operator.NOT_CONTAINS && value?.length === 1 ? value[0] : value;

	const filter = { key, op: op as Operator, value: filterValue, relative, group };
	filterObj.C.push(filter);
}

/** 
 * checks if 
 * 1. the spec is a string, 
 * 2. the string has no format or is multiline text (TextArea)
 * 3. check if queryKey is not equal to "Node Name" because if the querySpec.id is equal to "Node Name", it is treated as a ref and should be allowed to combine. 
 */
function isStringFormatNoneOrMultiLineText(spec: Spec): boolean {
	return spec.type === VALUE_TYPE.STRING && (!spec.format || spec.format === types.FORMAT_TEXTAREA) && spec.querySpec.id !== ATTR_NAME;
}

/** reconstruct filter setting using spec to determine if the filter has multiple widgets */
function _reconstructFilterConditions(filterObj: ICustomViewFilterV1, key: string, value: string[], group: boolean[], specBuilder: SpecBuilder): void {
	const [filterKey, operator, relative] = key.split(",");
	const values = Array.from(new Set(value));

	const spec = specBuilder.getSpecForKey(filterKey);
	// For input types that have changed to “textfield”, do not combine.
	// last conditional - If we restore single select input type for strings with no format or multi line text format, do not combine.
	if (!spec.querySpec.allowFilteringBySelectionOfExistingValues || operator === xml.OP_MATCHES || operator === xml.OP_NOT_MATCHES || isStringFormatNoneOrMultiLineText(spec)) {
		values.forEach((item, index) => {
			_pushFilterToFilterConditions(filterObj, filterKey, operator, [item], relative === "true", group[index]);
		});
	} else {
		const filterOperator = values.length > 1 ? _operatorAfterCombiningFilters(operator) : operator;
		_pushFilterToFilterConditions(filterObj, filterKey, filterOperator, values, relative === "true", group[group.length - 1]);
	}
}

function _reconstructFilterConditionsWhenConnectedByAndOperator(filterObj: ICustomViewFilterV1, key: string, value: string[], group: boolean): void {
	const [filterKey, operator, relative] = key.split(",");
	_pushFilterToFilterConditions(filterObj, filterKey, operator, value, relative === "true", group);
}

export function getNumGroupingLevels(groupSettings: ICustomViewGroup): number {
	const { key1, key2 } = groupSettings;
	return key1 && key2 ? 2 : key1 || key2 ? 1 : 0;
}

/** extract the key from key string and return the spec */
function extractSpecFromKey(key: string, builder: SpecBuilder): Spec {
	const [filterKey, operator, relative] = key.split(",");
	const spec = builder.getSpecForKey(filterKey);
	return spec;
}

/** convert empty value filters to blank filters for custom view before 7.10 */
export function convertEmptyValueFiltersToBlankFilters(filterSettings: ICustomViewFilterSettings, specBuilder: SpecBuilder): ICustomViewFilterSettings {
	forEach(filterSettings, (value) => {
		const { C: rules } = value;

		rules?.forEach((condition) => {
			const group = getCustomViewFilterGroup(condition);
			if (group) {
				for (let i = 0; i < group.conditions.length; i++) {
					const { key, op } = group.conditions[i] as ICustomViewFilterCondition;
					if (!isBlankOperator(op)) {
						const spec = specBuilder.getSpecForKey(key);
						group.conditions[i].op = getOperator(spec, group.conditions[i]);
					}
				}
			} else {
				const { key, op } = condition as ICustomViewFilterCondition;
				if (!isBlankOperator(op)) {
					const spec = specBuilder.getSpecForKey(key);
					(condition as ICustomViewFilterCondition).op = getOperator(spec, condition as ICustomViewFilterCondition);
				}
			}
		});
	});
	return filterSettings;
}

/** If the value is empty, return the appropriate operator (original operator, is blank operator, or is not blank operator) */
function getOperator(spec: Spec, condition: ICustomViewFilterConditionV1 | ICustomViewFilterCondition): Operator {
	const { op, value } = condition;
	let operator = op;
	if (spec.type === VALUE_TYPE.ENUM) {
		// case 1: when spec is an enum and it is an enum with empty value, convert operator
		const enumValue = xml.enumGetText(value as string);
		if (enumValue === "") {
			const isNotBlank = isNotBlankOperator(operator);
			operator = spec.getBlankOperator(isNotBlank) as Operator;
		}
	} else if (spec.type === VALUE_TYPE.TIME) {
		// case 2: when spec is a time attribute and its value is blank and not blank, convert operator
		if (value[2] === DateRangeSelection.BLANKS || value[2] === DateRangeSelection.NONBLANKS) {
			// value[2] is a DataRangeSelection that determines whether the filter is blank or not blank
			const isNotBlank = value[2] === DateRangeSelection.NONBLANKS;
			operator = spec.getBlankOperator(isNotBlank) as Operator;
		}
	} else if (value === "" || isNullOrUndefined(value)) {
		// case 3: when value is an empty string or null, convert operator
		if (!isBlankOperator(operator)) {
			const isNotBlank = isNotBlankOperator(operator);
			operator = spec.getBlankOperator(isNotBlank) as Operator;
		}
	}

	// case 4: return original operator
	return operator;
}